import { IGoal } from "features/onboarding/types"
import { useEffect, useState } from "react"
import {
  CALORIES_PER_CARBS,
  CALORIES_PER_FAT,
  CALORIES_PER_PROTEIN,
  getCaloriesFromMacrosGram,
  getCaloriesPercentFromMacrosGram,
  getMarcoGramFromCaloriesPercent,
} from "./helper"

export interface IMarcroPercentAndGram {
  proteinCaloriesPercent: number
  fatCaloriesPercent: number
  carbsCaloriesPercent: number
  proteinGram: null | number
  fatGram: null | number
  carbsGram: null | number
}

const useManualEditGoalHandlers = (goal?: IGoal) => {
  useEffect(() => {
    if (goal) {
      const { caloriesPerDay, carbsGram, proteinGram, fatGram } = goal
      const roundedProteinGram = Math.round(proteinGram)
      const roundedFatGram = Math.round(fatGram)
      const roundedCarbsGram = Math.round(carbsGram)

      setCalories(Math.round(caloriesPerDay))
      setProteinGram(roundedProteinGram)
      setFatGram(roundedFatGram)
      setCarbsGram(roundedCarbsGram)

      updateCaloriesPercentWhenMacrosGramChange({
        proteinGram: roundedProteinGram,
        fatGram: roundedFatGram,
        carbsGram: roundedCarbsGram,
      })
    }
  }, [goal])

  const [calories, setCalories] = useState<null | number>(null)
  const [isCaloriesError, setIsCaloriesError] = useState(false)

  useEffect(() => {
    const updateCaloriesError = (value: null | number) => {
      if (value === null || value <= 0 || value >= 10000) {
        setIsCaloriesError(true)
      } else {
        setIsCaloriesError(false)
      }
    }

    updateCaloriesError(calories)
  }, [calories])

  const updateMacroGramsFromCalories = (calories: null | number) => {
    const nextProteinGram = getMarcoGramFromCaloriesPercent({
      calories,
      percent: proteinCaloriesPercent,
      macroCaloriesPerGram: CALORIES_PER_PROTEIN,
    })

    const nextFatGram = getMarcoGramFromCaloriesPercent({
      calories,
      percent: fatCaloriesPercent,
      macroCaloriesPerGram: CALORIES_PER_FAT,
    })
    const nextCarbsGram = getMarcoGramFromCaloriesPercent({
      calories,
      percent: carbsCaloriesPercent,
      macroCaloriesPerGram: CALORIES_PER_CARBS,
    })

    setProteinGram(nextProteinGram)
    setFatGram(nextFatGram)
    setCarbsGram(nextCarbsGram)
  }

  const onCaloriesChange = (nextCalories: null | number) => {
    setCalories(nextCalories)
    updateMacroGramsFromCalories(nextCalories)
  }

  const updateCaloriesWhenMacrosGramChange = ({
    proteinGram,
    fatGram,
    carbsGram,
  }: {
    proteinGram: null | number
    fatGram: null | number
    carbsGram: null | number
  }) => {
    const caloriesFromMacros = getCaloriesFromMacrosGram({
      proteinGram,
      fatGram,
      carbsGram,
    })
    setCalories(caloriesFromMacros)
    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram,
      fatGram,
      carbsGram,
    })
  }

  const updateCaloriesPercentWhenMacrosGramChange = ({
    proteinGram,
    fatGram,
    carbsGram,
  }: {
    proteinGram: null | number
    fatGram: null | number
    carbsGram: null | number
  }) => {
    const { proteinPercent, fatPercent, carbPercent } =
      getCaloriesPercentFromMacrosGram({
        proteinGram,
        fatGram,
        carbsGram,
      })
    setProteinCaloriesPercent(proteinPercent)
    setFatCaloriesPercent(fatPercent)
    setCarbsCaloriesPercent(carbPercent)
  }

  const [proteinGram, setProteinGram] = useState<null | number>(null)
  const [proteinCaloriesPercent, setProteinCaloriesPercent] = useState(
    goal ? Math.round(goal.protein * 100) : 0,
  )
  const [isProteinGramError, setIsProteinGramError] = useState(false)

  const onProteinGramChange = (value: null | number) => {
    setProteinGram(value)
    setIsProteinGramError(value === null ? true : false)

    updateCaloriesWhenMacrosGramChange({
      proteinGram: value,
      fatGram,
      carbsGram,
    })

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram: value,
      fatGram,
      carbsGram,
    })
  }

  const [fatGram, setFatGram] = useState<null | number>(null)
  const [fatCaloriesPercent, setFatCaloriesPercent] = useState(
    goal ? Math.round(goal.fat * 100) : 0,
  )
  const [isFatGramError, setIsFatGramError] = useState(false)
  const onFatGramChange = (value: null | number) => {
    setFatGram(value)
    setIsFatGramError(value === null ? true : false)

    updateCaloriesWhenMacrosGramChange({
      proteinGram,
      fatGram: value,
      carbsGram,
    })

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram,
      fatGram: value,
      carbsGram,
    })
  }

  const [carbsGram, setCarbsGram] = useState<null | number>(null)
  const [isCarbsGramError, setIsCarbsGramError] = useState(false)

  const [carbsCaloriesPercent, setCarbsCaloriesPercent] = useState(
    goal ? Math.round(goal.carb * 100) : 0,
  )
  const onCarbsGramChange = (value: null | number) => {
    setCarbsGram(value)
    setIsCarbsGramError(value === null ? true : false)

    updateCaloriesWhenMacrosGramChange({
      proteinGram,
      fatGram,
      carbsGram: value,
    })

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram,
      fatGram,
      carbsGram: value,
    })
  }

  const onProteinFatCarbsPercentAndGramChange = ({
    proteinCaloriesPercent,
    fatCaloriesPercent,
    carbsCaloriesPercent,
    proteinGram,
    fatGram,
    carbsGram,
  }: IMarcroPercentAndGram) => {
    setProteinCaloriesPercent(proteinCaloriesPercent)
    setFatCaloriesPercent(fatCaloriesPercent)
    setCarbsCaloriesPercent(carbsCaloriesPercent)

    setProteinGram(proteinGram)
    setFatGram(fatGram)
    setCarbsGram(carbsGram)
  }

  const hasMacroPercents =
    proteinCaloriesPercent + fatCaloriesPercent + carbsCaloriesPercent > 0

  useEffect(() => {
    if (!hasMacroPercents && goal) {
      setProteinCaloriesPercent(100)
    }
  }, [hasMacroPercents, goal])

  return {
    calories,
    proteinGram,
    fatGram,
    carbsGram,
    proteinCaloriesPercent,
    fatCaloriesPercent,
    carbsCaloriesPercent,
    isCaloriesError,
    isProteinGramError,
    isFatGramError,
    isCarbsGramError,
    onCaloriesChange,
    onProteinGramChange,
    onFatGramChange,
    onCarbsGramChange,

    onProteinFatCarbsPercentAndGramChange,
  }
}

export default useManualEditGoalHandlers
